import { formatPhone, type Family, type FamilyLinkCheck, type FamilyLinkSide } from "@teamna/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Callout, InlineError, ListRow, RadioGroup, SearchField, Sheet, showToast } from "../../components/index.ts";
import { capital, familyName, type Words } from "../families/model.ts";
import type { FamilyActions } from "../families/types.ts";

/**
 * "Link a parent who is already here" (#ai-feedback 2026-10-26, card #14). Two parents of the
 * business are one family. The owner finds the other parent, then a check comes before the link:
 * which family stays (its account pays and gets the reminders), and what moves. The other family's
 * children, packages, bookings and payments move to the family that stays, and its parent becomes a
 * second adult with full access. Each parent keeps their own number. No message goes to either one.
 */

/** The families the search shows: not this one, and only one with a parent to name. */
export function linkCandidates(families: Family[], current: Family, query: string): Family[] {
  const words = query.trim().toLocaleLowerCase();
  const digits = query.replace(/\D/g, "");
  if (!words) return [];
  return families
    .filter((row) => row.id !== current.id && !row.adults.some((adult) => current.adults.some((mine) => mine.userId === adult.userId)))
    .filter((row) =>
      row.adults.some(
        (adult) => adult.name.toLocaleLowerCase().includes(words) || (digits.length >= 3 && (adult.phoneE164 ?? "").replace(/\D/g, "").includes(digits)),
      ) || row.children.some((child) => child.name.toLocaleLowerCase().includes(words)),
    )
    .slice(0, 8);
}

/**
 * The words of the link: "family" where members book for their children, "account" where clients
 * book for themselves (F2). The server rules are the same for both.
 */
export function linkWords(words: Words) {
  return words.forChildren
    ? { one: "family", many: "families", ofName: (name: string) => `the family of ${name}` }
    : { one: "account", many: "accounts", ofName: (name: string) => `the account of ${name}` };
}

function sideLines(side: FamilyLinkSide, words: Words): string[] {
  const lines = [`${capital(words.members)}: ${side.adults.join(", ") || "none"}`];
  lines.push(`${capital(words.participants)}: ${side.children.map((child) => child.name).join(", ") || "none"}`);
  if (side.packages > 0) lines.push(`${side.packages} ${side.packages === 1 ? "package" : "packages"}, ${side.sessionsLeft} ${side.sessionsLeft === 1 ? "session" : "sessions"} left`);
  if (side.upcomingBookings > 0) lines.push(`${side.upcomingBookings} ${side.upcomingBookings === 1 ? "session" : "sessions"} booked`);
  return lines;
}

export function LinkFamilyPanel({
  family,
  families,
  words,
  actions,
  startWith,
  onClose,
}: {
  family: Family;
  /** Every family of the business, for the search. */
  families: Family[];
  words: Words;
  actions: FamilyActions;
  /** The other family, when the panel opens from "This number is a family of its own here." */
  startWith?: string | null;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [check, setCheck] = useState<FamilyLinkCheck | null>(null);
  const [keep, setKeep] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const found = useMemo(() => linkCandidates(families, family, query), [families, family, query]);

  async function choose(otherId: string) {
    setBusy(true);
    setError(null);
    try {
      const answer = await actions.checkLink([family.id, otherId]);
      setCheck(answer);
      // This family stays, unless only the other one can.
      const open = answer.sides.find((side) => side.keepRefusal === null);
      setKeep(answer.sides[0].keepRefusal === null ? answer.sides[0].familyId : (open?.familyId ?? null));
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  // Opened from a refused number: go straight to the check of that family, once.
  const started = useRef(false);
  useEffect(() => {
    if (!startWith || started.current) return;
    started.current = true;
    void choose(startWith);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startWith]);

  async function link() {
    if (!check || !keep) return;
    const merge = check.sides.find((side) => side.familyId !== keep)!;
    const kept = check.sides.find((side) => side.familyId === keep)!;
    setBusy(true);
    setError(null);
    try {
      const answer = await actions.linkFamilies(keep, merge.familyId);
      showToast(`${kept.adults[0] ?? capital(words.member)} and ${merge.adults[0] ?? `the other ${words.member}`} are one ${unit.one} now.`, {
        action: { label: "Undo", onClick: () => void actions.undoLink(answer.linkId).catch((failure: unknown) => showToast(undoFailure(failure))) },
      });
      onClose();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  const unit = linkWords(words);
  const merging = check && keep ? check.sides.find((side) => side.familyId !== keep) : null;
  const staying = check && keep ? check.sides.find((side) => side.familyId === keep) : null;

  return (
    <Sheet
      open
      onClose={onClose}
      title={`Link a ${words.member} who is already here`}
      subtitle={check ? "Check before you link" : `Find the other ${words.member} by name or number.`}
      actions={
        check ? (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void link()} loading={busy} disabled={!keep}>
              Link the {unit.many}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setCheck(null);
                setKeep(null);
                setError(null);
              }}
            >
              Back
            </Button>
          </div>
        ) : null
      }
    >
      {error ? <InlineError error={error} /> : null}
      {check ? (
        <div className="flex flex-col gap-4">
          <RadioGroup
            legend={`Which ${unit.one} stays?`}
            hint={`The ${unit.one} that stays pays and gets the reminders. Both ${words.members} can see, book and pay for every ${words.participant}.`}
            value={keep}
            onChange={setKeep}
            options={check.sides.map((side) => ({
              value: side.familyId,
              label: `Keep ${unit.ofName(side.adults[0] ?? `this ${words.member}`)}`,
              hint: side.keepRefusal ?? sideLines(side, words).join(". "),
              disabled: side.keepRefusal !== null,
            }))}
          />
          {check.sides.every((side) => side.keepRefusal !== null) ? (
            <Callout tone="late" title={`These ${unit.many} cannot be linked now`}>
              {check.sides[0].keepRefusal}
            </Callout>
          ) : null}
          {merging && staying ? (
            <p className="text-[14px] text-ink-2">
              The {words.participants}, packages, bookings and payments of {merging.adults[0] ?? `the other ${words.member}`} move to{" "}
              {unit.ofName(staying.adults[0] ?? `this ${words.member}`)}. {merging.adults[0] ?? `The other ${words.member}`} becomes a second {words.member}. Each {words.member} keeps
              their own number to sign in. No message goes to either {words.member}.
            </p>
          ) : null}
          {check.sameNameChildren.length > 0 ? (
            <Callout tone="late" title={`Two ${words.participants} with the same name`}>
              Both {unit.many} have {check.sameNameChildren.join(" and ")}. Teamna does not join them. If it is the same {words.participant}, remove one after the
              link.
            </Callout>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <SearchField label={`Search ${words.members}`} value={query} onChange={setQuery} size="regular" />
          {query.trim() && found.length === 0 ? <p className="text-[14px] text-ink-2">No {words.member} matches.</p> : null}
          <div className="flex flex-col divide-y">
            {found.map((row) => (
              <ListRow
                key={row.id}
                title={familyName(row)}
                subtitle={[
                  row.adults[0]?.phoneE164 ? formatPhone(row.adults[0].phoneE164) : null,
                  row.children.length ? row.children.map((child) => child.name).join(", ") : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                disabled={busy}
                onSelect={() => void choose(row.id)}
              />
            ))}
          </div>
        </div>
      )}
    </Sheet>
  );
}

function undoFailure(failure: unknown): string {
  const detail = (failure as { detail?: unknown } | null)?.detail;
  return typeof detail === "string" ? detail : "Undo did not work. Try again.";
}
